import { MoreHoriz } from "@mui/icons-material";
import { Backdrop, Button, Dialog, Menu, MenuItem } from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useState } from "react";

import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import { Subscription } from "models/User";
import {
  removePaymentMethod,
  setDefaultPaymentMethod,
} from "services/SubscriptionService";
import { formatLongText } from "utils";

import styles from "./Card.module.scss";

export const MasterCardLogo = () => {
  return (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      viewBox="0 0 152.4 108"
      xmlSpace="preserve"
    >
      <rect width="152.4" height="108" fill="none" />
      <g>
        <rect x="60.4" y="25.7" width="31.5" height="56.6" fill="#FF5F00" />
        <path
          fill="#EB001B"
          d="M62.4,54c0-11,5.1-21.5,13.7-28.3c-15.6-12.3-38.3-9.6-50.6,6.1C13.3,47.4,16,70,31.7,82.3
            c13.1,10.3,31.4,10.3,44.5,0C67.5,75.5,62.4,65,62.4,54z"
        />
        <path
          fill="#F79E1B"
          d="M134.4,54c0,19.9-16.1,36-36,36c-8.1,0-15.9-2.7-22.2-7.7c15.6-12.3,18.3-34.9,6-50.6
            c-1.8-2.2-3.8-4.3-6-6c15.6-12.3,38.3-9.6,50.5,6.1C131.7,38.1,134.4,45.9,134.4,54z"
        />
      </g>
    </svg>
  );
};

export const VisaLogo = () => {
  return (
    <svg
      width="800px"
      height="800px"
      viewBox="0 -140 780 780"
      enableBackground="new 0 0 780 500"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="780" height="500" fill="#0E4595" />
      <path
        d="m293.2 348.73l33.361-195.76h53.36l-33.385 195.76h-53.336zm246.11-191.54c-10.57-3.966-27.137-8.222-47.822-8.222-52.725 0-89.865 26.55-90.18 64.603-0.299 28.13 26.514 43.822 46.752 53.186 20.771 9.595 27.752 15.714 27.654 24.283-0.131 13.121-16.586 19.116-31.922 19.116-21.357 0-32.703-2.967-50.227-10.276l-6.876-3.11-7.489 43.823c12.463 5.464 35.51 10.198 59.438 10.443 56.09 0 92.5-26.246 92.916-66.882 0.199-22.269-14.016-39.216-44.801-53.188-18.65-9.055-30.072-15.099-29.951-24.268 0-8.137 9.668-16.839 30.557-16.839 17.449-0.27 30.09 3.535 39.938 7.5l4.781 2.26 7.232-42.429m137.31-4.223h-41.232c-12.773 0-22.332 3.487-27.941 16.234l-79.244 179.4h56.031s9.16-24.123 11.232-29.418c6.125 0 60.555 0.084 68.338 0.084 1.596 6.853 6.49 29.334 6.49 29.334h49.514l-43.188-195.64zm-65.418 126.41c4.412-11.279 21.26-54.723 21.26-54.723-0.316 0.522 4.379-11.334 7.074-18.684l3.605 16.879s10.219 46.729 12.354 56.528h-44.293zm-363.3-126.41l-52.24 133.5-5.567-27.13c-9.725-31.273-40.025-65.155-73.898-82.118l47.766 171.2 56.456-0.064 84.004-195.39h-56.521"
        fill="#ffffff"
      />
      <path
        d="m146.92 152.96h-86.041l-0.681 4.073c66.938 16.204 111.23 55.363 129.62 102.41l-18.71-89.96c-3.23-12.395-12.597-16.094-24.186-16.527"
        fill="#F2AE14"
      />
    </svg>
  );
};

export const AmericanExpressLogo = () => {
  return (
    <svg
      width="800px"
      height="800px"
      viewBox="0 -9 58 58"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.5"
        y="0.5"
        width="57"
        height="39"
        rx="3.5"
        fill="#006FCF"
        stroke="#F3F3F3"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.8632 28.8937V20.6592H21.1869L22.1872 21.8787L23.2206 20.6592H57.0632V28.3258C57.0632 28.3258 56.1782 28.8855 55.1546 28.8937H36.4152L35.2874 27.5957V28.8937H31.5916V26.6779C31.5916 26.6779 31.0867 26.9872 29.9953 26.9872H28.7373V28.8937H23.1415L22.1426 27.6481L21.1284 28.8937H11.8632ZM1 14.4529L3.09775 9.86914H6.7256L7.9161 12.4368V9.86914H12.4258L13.1346 11.7249L13.8216 9.86914H34.0657V10.8021C34.0657 10.8021 35.1299 9.86914 36.8789 9.86914L43.4474 9.89066L44.6173 12.4247V9.86914H48.3913L49.43 11.3247V9.86914H53.2386V18.1037H49.43L48.4346 16.6434V18.1037H42.8898L42.3321 16.8056H40.8415L40.293 18.1037H36.5327C35.0277 18.1037 34.0657 17.1897 34.0657 17.1897V18.1037H28.3961L27.2708 16.8056V18.1037H6.18816L5.63093 16.8056H4.14505L3.59176 18.1037H1V14.4529ZM1.01082 17.05L3.84023 10.8843H5.98528L8.81199 17.05H6.92932L6.40997 15.8154H3.37498L2.85291 17.05H1.01082ZM5.81217 14.4768L4.88706 12.3192L3.95925 14.4768H5.81217ZM9.00675 17.049V10.8832L11.6245 10.8924L13.147 14.8676L14.6331 10.8832H17.2299V17.049H15.5853V12.5058L13.8419 17.049H12.3996L10.6514 12.5058V17.049H9.00675ZM18.3552 17.049V10.8832H23.7219V12.2624H20.0171V13.3171H23.6353V14.6151H20.0171V15.7104H23.7219V17.049H18.3552ZM24.674 17.05V10.8843H28.3339C29.5465 10.8843 30.6331 11.5871 30.6331 12.8846C30.6331 13.9938 29.717 14.7082 28.8289 14.7784L30.9929 17.05H28.9831L27.0111 14.8596H26.3186V17.05H24.674ZM28.1986 12.2635H26.3186V13.5615H28.223C28.5526 13.5615 28.9776 13.3221 28.9776 12.9125C28.9776 12.5941 28.6496 12.2635 28.1986 12.2635ZM32.9837 17.049H31.3045V10.8832H32.9837V17.049ZM36.9655 17.049H36.603C34.8492 17.049 33.7844 15.754 33.7844 13.9915C33.7844 12.1854 34.8373 10.8832 37.052 10.8832H38.8698V12.3436H36.9856C36.0865 12.3436 35.4507 13.0012 35.4507 14.0067C35.4507 15.2008 36.1777 15.7023 37.2251 15.7023H37.6579L36.9655 17.049ZM37.7147 17.05L40.5441 10.8843H42.6892L45.5159 17.05H43.6332L43.1139 15.8154H40.0789L39.5568 17.05H37.7147ZM42.5161 14.4768L41.591 12.3192L40.6632 14.4768H42.5161ZM45.708 17.049V10.8832H47.7989L50.4687 14.7571V10.8832H52.1134V17.049H50.09L47.3526 13.0737V17.049H45.708ZM12.9885 27.8391V21.6733H18.3552V23.0525H14.6504V24.1072H18.2686V25.4052H14.6504V26.5005H18.3552V27.8391H12.9885ZM39.2853 27.8391V21.6733H44.6519V23.0525H40.9472V24.1072H44.5481V25.4052H40.9472V26.5005H44.6519V27.8391H39.2853ZM18.5635 27.8391L21.1765 24.7942L18.5012 21.6733H20.5733L22.1665 23.6026L23.7651 21.6733H25.756L23.1159 24.7562L25.7338 27.8391H23.6621L22.1151 25.9402L20.6057 27.8391H18.5635ZM25.9291 27.8401V21.6744H29.5619C31.0525 21.6744 31.9234 22.5748 31.9234 23.7482C31.9234 25.1647 30.8131 25.893 29.3482 25.893H27.617V27.8401H25.9291ZM29.4402 23.0687H27.617V24.4885H29.4348C29.9151 24.4885 30.2517 24.1901 30.2517 23.7786C30.2517 23.3406 29.9134 23.0687 29.4402 23.0687ZM32.6375 27.8391V21.6733H36.2973C37.51 21.6733 38.5966 22.3761 38.5966 23.6736C38.5966 24.7828 37.6805 25.4972 36.7923 25.5675L38.9563 27.8391H36.9465L34.9746 25.6486H34.2821V27.8391H32.6375ZM36.1621 23.0525H34.2821V24.3505H36.1864C36.5161 24.3505 36.9411 24.1112 36.9411 23.7015C36.9411 23.3831 36.6131 23.0525 36.1621 23.0525ZM45.4137 27.8391V26.5005H48.7051C49.1921 26.5005 49.403 26.2538 49.403 25.9833C49.403 25.7241 49.1928 25.462 48.7051 25.462H47.2177C45.9249 25.462 45.2048 24.7237 45.2048 23.6153C45.2048 22.6267 45.8642 21.6733 47.7854 21.6733H50.9881L50.2956 23.0606H47.5257C46.9962 23.0606 46.8332 23.321 46.8332 23.5697C46.8332 23.8253 47.0347 24.1072 47.4392 24.1072H48.9972C50.4384 24.1072 51.0638 24.8734 51.0638 25.8768C51.0638 26.9555 50.367 27.8391 48.9188 27.8391H45.4137ZM51.2088 27.8391V26.5005H54.5002C54.9873 26.5005 55.1981 26.2538 55.1981 25.9833C55.1981 25.7241 54.9879 25.462 54.5002 25.462H53.0129C51.72 25.462 51 24.7237 51 23.6153C51 22.6267 51.6594 21.6733 53.5806 21.6733H56.7833L56.0908 23.0606H53.3209C52.7914 23.0606 52.6284 23.321 52.6284 23.5697C52.6284 23.8253 52.8298 24.1072 53.2343 24.1072H54.7924C56.2336 24.1072 56.859 24.8734 56.859 25.8768C56.859 26.9555 56.1621 27.8391 54.7139 27.8391H51.2088Z"
        fill="white"
      />
    </svg>
  );
};

export const DiscoverLogo = () => {
  return (
    <svg
      width="800px"
      height="800px"
      viewBox="0 -140 780 780"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fillRule="evenodd">
        <path
          d="M54.992 0C24.627 0 0 24.63 0 55.004v390.992C0 476.376 24.619 501 54.992 501h670.016C755.373 501 780 476.37 780 445.996V55.004C780 24.624 755.381 0 725.008 0H54.992z"
          fill="#4D4D4D"
        />

        <path
          d="M327.152 161.893c8.837 0 16.248 1.784 25.268 6.09v22.751c-8.544-7.863-15.955-11.154-25.756-11.154-19.264 0-34.414 15.015-34.414 34.05 0 20.075 14.681 34.196 35.37 34.196 9.312 0 16.586-3.12 24.8-10.857v22.763c-9.341 4.14-16.911 5.776-25.756 5.776-31.278 0-55.582-22.596-55.582-51.737 0-28.826 24.951-51.878 56.07-51.878zm-97.113.627c11.546 0 22.11 3.72 30.943 10.994l-10.748 13.248c-5.35-5.646-10.41-8.028-16.564-8.028-8.853 0-15.3 4.745-15.3 10.989 0 5.354 3.619 8.188 15.944 12.482 23.365 8.044 30.29 15.176 30.29 30.926 0 19.193-14.976 32.553-36.32 32.553-15.63 0-26.994-5.795-36.458-18.872l13.268-12.03c4.73 8.61 12.622 13.222 22.42 13.222 9.163 0 15.947-5.952 15.947-13.984 0-4.164-2.055-7.734-6.158-10.258-2.066-1.195-6.158-2.977-14.2-5.647-19.291-6.538-25.91-13.527-25.91-27.185 0-16.225 14.214-28.41 32.846-28.41zm234.723 1.728h22.437l28.084 66.592 28.446-66.592h22.267l-45.494 101.686h-11.053l-44.687-101.686zm-397.348.152h30.15c33.312 0 56.534 20.382 56.534 49.641 0 14.59-7.104 28.696-19.118 38.057-10.108 7.901-21.626 11.445-37.574 11.445H67.414V164.4zm96.135 0h20.54v99.143h-20.54V164.4zm411.734 0h58.252v16.8H595.81v22.005h36.336v16.791h-36.336v26.762h37.726v16.785h-58.252V164.4zm71.858 0h30.455c23.69 0 37.265 10.71 37.265 29.272 0 15.18-8.514 25.14-23.986 28.105l33.148 41.766h-25.26l-28.429-39.828h-2.678v39.828h-20.515V164.4zm20.515 15.616v30.025h6.002c13.117 0 20.069-5.362 20.069-15.328 0-9.648-6.954-14.697-19.745-14.697h-6.326zM87.94 181.199v65.559h5.512c13.273 0 21.656-2.394 28.11-7.88 7.103-5.955 11.376-15.465 11.376-24.98 0-9.499-4.273-18.725-11.376-24.681-6.785-5.78-14.837-8.018-28.11-8.018H87.94z"
          fill="#FFF"
        />

        <path
          d="m415.13 161.21c30.941 0 56.022 23.58 56.022 52.709v0.033c0 29.13-25.081 52.742-56.021 52.742s-56.022-23.613-56.022-52.742v-0.033c0-29.13 25.082-52.71 56.022-52.71zm364.85 127.15c-26.05 18.33-221.08 149.34-558.75 212.62h503.76c30.365 0 54.992-24.63 54.992-55.004v-157.62z"
          fill="#F47216"
        />
      </g>
    </svg>
  );
};

export const PaypalLogo = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      aria-label="PayPal"
      role="img"
      viewBox="0 0 512 512"
    >
      <rect width="512" height="512" rx="15%" fill="#ffffff" />
      <path
        fill="#002c8a"
        d="M377 184.8L180.7 399h-72c-5 0-9-5-8-10l48-304c1-7 7-12 14-12h122c84 3 107 46 92 112z"
      />
      <path
        fill="#009be1"
        d="M380.2 165c30 16 37 46 27 86-13 59-52 84-109 85l-16 1c-6 0-10 4-11 10l-13 79c-1 7-7 12-14 12h-60c-5 0-9-5-8-10l22-143c1-5 182-120 182-120z"
      />
      <path
        fill="#001f6b"
        d="M197 292l20-127a14 14 0 0 1 13-11h96c23 0 40 4 54 11-5 44-26 115-128 117h-44c-5 0-10 4-11 10z"
      />
    </svg>
  );
};
const GetCardImage = ({ brand }: { brand: string }) => {
  switch (brand) {
    case "paypal":
      return (
        <div className={styles.brandLogo}>
          <PaypalLogo />
        </div>
      );
    case "mastercard":
      return (
        <div className={styles.brandLogo}>
          <MasterCardLogo />
        </div>
      );
    case "visa":
      return (
        <div className={styles.brandLogo}>
          <VisaLogo />
        </div>
      );
    case "amex":
      return (
        <div className={styles.brandLogo}>
          <AmericanExpressLogo />
        </div>
      );
    case "discover":
      return (
        <div className={styles.brandLogo}>
          <DiscoverLogo />
        </div>
      );
    default:
      return <div className={styles.brandImg}>{formatLongText(brand, 10)}</div>;
  }
};

interface CardProps {
  paymentMethod: PaymentMethod;
  onClick: (paymentMethod: PaymentMethod) => void;
  isDefault?: boolean;
  isSelected?: boolean;
  setDefaultMethod?: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
  onRemove: () => void;
  hideOptions?: boolean;
  subscription: Subscription;
}

const Card: React.FC<CardProps> = ({
  paymentMethod,
  onClick,
  isDefault,
  isSelected,
  setDefaultMethod,
  onRemove,
  hideOptions,
  subscription,
}) => {
  const [cardAnchorEl, setCardAnchorEl] = useState<null | HTMLElement>(null);
  const [showCardDeleteModal, setShowCardDeleteModal] = useState(false);
  const [confirmDefaultModal, setConfirmDefaultModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  return (
    <>
      <div
        className={cx(styles.container, isSelected && styles.isSelected)}
        onClick={() => onClick(paymentMethod)}
      >
        <div className={styles.left}>
          <GetCardImage
            brand={paymentMethod.card?.brand || paymentMethod.type || ""}
          />
          <div className={styles.details}>
            <div className={styles.cardNumber}>
              {paymentMethod.card
                ? `**** **** **** ${paymentMethod.card?.last4}`
                : paymentMethod.type}
            </div>
            {paymentMethod.card && (
              <div
                className={styles.cardExpiry}
              >{`Exp. date: ${paymentMethod.card?.exp_month}/${paymentMethod.card?.exp_year}`}</div>
            )}
          </div>
        </div>
        <div className={styles.right}>
          {setDefaultMethod &&
            (isDefault ? (
              <div className={styles.isDefault}>Default</div>
            ) : (
              <Button
                className={styles.setDefault}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDefaultModal(true);
                }}
              >
                Set as default
              </Button>
            ))}

          <Button
            disabled={hideOptions || isDefault}
            className={styles.threeDot}
            onClick={(e) => {
              e.stopPropagation();
              setCardAnchorEl(e.target as HTMLElement);
            }}
          >
            <MoreHoriz />
          </Button>
        </div>
      </div>

      <Menu
        id="basic-menu"
        anchorEl={cardAnchorEl}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        open={!!cardAnchorEl}
        onClose={() => {
          setCardAnchorEl(null);
        }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#111113",
            borderRadius: "7px",
            border: "1px solid #CCCCDC1F",
            padding: "8px 6px",
            width: "184px",
            boxShadow: "0px 8px 24px 0px #010409",
          },
        }}
      >
        <MenuItem
          className={styles.menuItem}
          onClick={() => {
            setCardAnchorEl(null);
            setShowCardDeleteModal(true);
          }}
        >
          <DeleteIcon style={{ marginRight: "8px" }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={showCardDeleteModal && !isLoading}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">Delete Payment Method</h3>
            <button
              onClick={() => {
                setShowCardDeleteModal(false);
              }}
              type="button"
              className="closeButton"
            >
              <XMark />
            </button>
          </div>
          <div className={styles.confirmationText}>
            <p>
              Are you sure you want to delete this payment method{" "}
              {paymentMethod.card && (
                <strong>ending with {paymentMethod.card?.last4}</strong>
              )}{" "}
              ?
            </p>
          </div>
          <div className="modalActions">
            <Button
              type="button"
              className={styles.noBackgroundButton}
              onClick={() => {
                setShowCardDeleteModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={styles.outlineButtonRed}
              onClick={() => {
                setIsLoading(true);
                removePaymentMethod(subscription?.id, paymentMethod.id)
                  .then(() => {
                    onRemove();
                    setShowCardDeleteModal(false);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={confirmDefaultModal && !isLoading}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">Update Payment Method</h3>
            <button
              onClick={() => {
                setConfirmDefaultModal(false);
              }}
              type="button"
              className="closeButton"
            >
              <XMark />
            </button>
          </div>
          <div className={styles.confirmationText}>
            <p>
              Are you sure you want to set this payment method{" "}
              {paymentMethod.card && (
                <strong>ending with {paymentMethod.card?.last4}</strong>
              )}{" "}
              as default ?
            </p>
            <br />
            <p>It will be applied to your next billing period.</p>
          </div>
          <div className="modalActions">
            <Button
              type="button"
              className={styles.noBackgroundButton}
              onClick={() => {
                setConfirmDefaultModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={styles.slapShotButton}
              onClick={() => {
                setIsLoading(true);
                setDefaultPaymentMethod(subscription?.id, paymentMethod.id)
                  .then((data) => {
                    setDefaultMethod?.(data);
                    setConfirmDefaultModal(false);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>

      <Backdrop open={isLoading} style={{ zIndex: 1 }}>
        <img src="/loading.gif" alt="" />
      </Backdrop>
    </>
  );
};

export default Card;
